import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Typography,
  Button,
  Table,
  message,
  Image,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import DefaultImage from "../../../Assets/images/Default-image.png";
import {
  fetchDietaryPreferencesDet,
  fetchCuisinesDet,
  fetchCategoriesDet,
  deleteItem,
} from "../useCategoryAPI";
import DeleteModalCategory from "./DeleteModalCategory";
import AddDietCusiniesModal from "./AddDietCusiniesModal";
import AddCategoryModal from "./AddCategoryModal";
const { Title, Text } = Typography;

const Category = () => {
  // Separate states for all sections
  const [dietaryItems, setDietaryItems] = useState([]);
  const [cuisineItems, setCuisineItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [section, setSection] = useState("");
  const [selectedDietary, setSelectedDietary] = useState(null); // Store selected dietary preference
  const [selectedCuisine, setSelectedCuisine] = useState(null); // Store selected cuisine
  const [dietaryPagination, setDietaryPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [cuisinePagination, setCuisinePagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [categoryPagination, setCategoryPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Fetching the Dietary Preferences from the API
  const fetchDietaryPreferences = async (page = 0) => {
    try {
      const data = await fetchDietaryPreferencesDet(
        page,
        dietaryPagination.pageSize
      );
      if (data && data?.diets) {
        setDietaryItems(data?.diets); // Set the dietary items from the response
        setDietaryPagination((prev) => ({
          ...prev,
          total: data?.pagination?.totalElements,
        }));
      } else {
        message.warning("No dietary data found.");
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to load dietary preferences."
      );
    }
  };

  // Fetching the Cuisines from the API
  const fetchCuisines = async (page = 0) => {
    try {
      const data = await fetchCuisinesDet(page, cuisinePagination.pageSize);
      if (data && data?.cuisines) {
        setCuisineItems(data?.cuisines); // Set the cuisines from the response
        setCuisinePagination((prev) => ({
          ...prev,
          total: data?.pagination?.totalElements,
        }));
      } else {
        message.warning("No cuisines data found.");
      }
    } catch (error) {
      message.error("Failed to load cuisines.");
      console.error("Error fetching cuisines:", error);
    }
  };

  // Fetching Categories from the API
  const fetchCategories = async (page = 0) => {
    try {
      const data = await fetchCategoriesDet(page, categoryPagination.pageSize);
      if (data && data?.categories) {
        setCategories(data?.categories); // Set categories data
        setCategoryPagination((prev) => ({
          ...prev,
          total: data?.pagination?.totalElements,
        }));
      } else {
        message.warning("No categories data found.");
      }
    } catch (error) {
      message.error("Failed to load categories.");
      console.error("Error fetching categories:", error);
    }
  };

  // Call the APIs when the component mounts or when the page changes
  useEffect(() => {
    fetchDietaryPreferences(dietaryPagination.current - 1);
    fetchCuisines(cuisinePagination.current - 1);
    fetchCategories(categoryPagination.current - 1);
  }, [
    dietaryPagination.current,
    cuisinePagination.current,
    categoryPagination.current,
  ]);

  const handleDelete = async (section, categoryId) => {
    try {
      if (!section || !categoryId) {
        message.error(
          "Section or Category is not been selected properly to delete"
        );
      }
      await deleteItem(section, categoryId);
      message.success(`Item deleted successfully from ${section}!`);
      setDeleteModalVisible(false);
      if (section === "Dietary Preference") {
        fetchDietaryPreferences(dietaryPagination.current - 1);
        setSelectedDietary(null);
      }
      if (section === "Cuisine") {
        fetchCuisines(cuisinePagination.current - 1);
        setSelectedCuisine(null);
      }
      if (section === "Category")
        fetchCategories(categoryPagination.current - 1);
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to delete the item."
      );
    }
  };

  const showDeleteModal = (item, section) => {
    setSelectedItem(item);
    setSection(section);
    setDeleteModalVisible(true);
  };

  const dietaryColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div
          style={{
            cursor: "pointer",
            color: "#1890ff",
            width: "100%",
            height: "100%",
          }}
          onClick={() => setSelectedDietary(record)} // Set the selected dietary on click
        >
          {text}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record, index) => (
        <DeleteOutlined
          style={{ color: "red", cursor: "pointer" }}
          onClick={() => showDeleteModal(record, "Dietary Preference")}
        />
      ),
    },
  ];

  const cuisineColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div
          style={{
            cursor: "pointer",
            color: "#1890ff",
            width: "100%",
            height: "100%",
          }}
          onClick={() => setSelectedCuisine(record)} // Set the selected cuisine on click
        >
          {text}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record, index) => (
        <DeleteOutlined
          style={{ color: "red", cursor: "pointer" }}
          onClick={() => showDeleteModal(record, "Cuisine")}
        />
      ),
    },
  ];

  const categoryColumns = [
    {
      title: "Category",
      dataIndex: "name",
      key: "category",
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record, index) => (
        <DeleteOutlined
          style={{ color: "red", cursor: "pointer" }}
          onClick={() => showDeleteModal(record, "Category")}
        />
      ),
    },
  ];

  const handleDietaryPaginationChange = (page) => {
    setDietaryPagination((prev) => ({
      ...prev,
      current: page,
    }));
  };

  const handleCuisinePaginationChange = (page) => {
    setCuisinePagination((prev) => ({
      ...prev,
      current: page,
    }));
  };

  const handleCategoryPaginationChange = (page) => {
    setCategoryPagination((prev) => ({
      ...prev,
      current: page,
    }));
  };

  const [showAddModalForDietCusines, setShowAddModalForDietCusines] =
    useState(false);
  const [currentCategory, setCurrentCategory] = useState("");
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);

  return (
    <div className="flex-grow-content" style={{ padding: "16px" }}>
      {/* Row for Dietary preferences */}
      <Row style={{ marginBottom: "32px" }}>
        <Col span={24}>
          <Title level={3} style={{ textAlign: "left" }}>
            Dietary Preferences
          </Title>
        </Col>
        <Col span={9} style={{ textAlign: "left" }}>
          {selectedDietary ? (
            <>
              <div
                style={{
                  width: "95%",
                  height: "200px",
                  display: "flex",
                  justifyContent: "left",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                <Image
                  src={selectedDietary.coverPhoto.fileUrl || DefaultImage} // Use the selected item's image
                  alt={selectedDietary.name}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
              <Title level={5} style={{ textAlign: "center" }}>
                {selectedDietary.name}
              </Title>
            </>
          ) : (
            <Text>Select a Dietary Preference</Text> // Show message if no item is selected
          )}
        </Col>
        <Col span={9} style={{ textAlign: "left" }}>
          <Table
            dataSource={dietaryItems.length > 0 ? dietaryItems : []} // Use dietaryItems state
            columns={dietaryColumns}
            pagination={{
              current: dietaryPagination.current,
              pageSize: dietaryPagination.pageSize,
              total: dietaryPagination.total,
              onChange: handleDietaryPaginationChange,
              showSizeChanger: false,
            }}
            style={{
              width: "85%",
              border: "1px solid #E1E1E1",
              borderRadius: "8px 8px 8px 8px",
              overflow: "hidden",
            }}
          />
        </Col>
        <Col span={6} style={{ textAlign: "left" }}>
          <Button
            className="primary-button"
            variant="contained"
            onClick={() => {
              setCurrentCategory("Dietary Preference");
              setShowAddModalForDietCusines(true);
            }}
          >
            Add
          </Button>
        </Col>
      </Row>

      {/* Row for Cuisines */}
      <Row style={{ marginBottom: "32px" }}>
        <Col span={24}>
          <Title level={3} style={{ textAlign: "left" }}>
            Cuisines
          </Title>
        </Col>
        <Col span={9} style={{ textAlign: "left" }}>
          {selectedCuisine ? (
            <>
              <div
                style={{
                  width: "95%",
                  height: "200px",
                  display: "flex",
                  justifyContent: "left",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                <Image
                  src={selectedCuisine.coverPhoto.fileUrl || DefaultImage} // Use the selected cuisine's image
                  alt={selectedCuisine.name}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
              <Title level={5} style={{ textAlign: "center" }}>
                {selectedCuisine.name}
              </Title>
            </>
          ) : (
            <Text>Select a Cuisine</Text> 
          )}
        </Col>

        {/* Table of cuisines */}
        <Col span={9} style={{ textAlign: "left" }}>
          <Table
            dataSource={cuisineItems.length > 0 ? cuisineItems : []} 
            columns={cuisineColumns}
            pagination={{
              current: cuisinePagination.current,
              pageSize: cuisinePagination.pageSize,
              total: cuisinePagination.total,
              onChange: handleCuisinePaginationChange,
              showSizeChanger: false,
            }}
            style={{
              width: "85%",
              border: "1px solid #E1E1E1",
              borderRadius: "8px 8px 8px 8px",
              overflow: "hidden",
            }}
          />
        </Col>

        {/* Right side: Add button (existing) */}
        <Col span={6} style={{ textAlign: "left" }}>
          <Button
            className="primary-button"
            variant="contained"
            onClick={() => {
              setCurrentCategory("Cuisine");
              setShowAddModalForDietCusines(true);
            }}
          >
            Add
          </Button>
        </Col>
      </Row>

      {/* Categories Section */}
      <Row style={{ marginBottom: "32px" }}>
        <Col span={24}>
          <Title level={3} style={{ textAlign: "left" }}>
            Categories
          </Title>
        </Col>
        <Col span={8} style={{ textAlign: "left" }}>
          <Table
            dataSource={categories?.map((item, index) => ({
              key: index,
              name: item.name, 
              id: item.id,
            }))}
            columns={categoryColumns}
            pagination={{
              current: categoryPagination.current,
              pageSize: categoryPagination.pageSize,
              total: categoryPagination.total,
              onChange: handleCategoryPaginationChange,
              showSizeChanger: false,
            }}
            style={{
              width: "85%",
              border: "1px solid #E1E1E1",
              borderRadius: "8px 8px 8px 8px",
              overflow: "hidden",
            }}
          />
        </Col>
        <Col span={8} style={{ textAlign: "left" }}>
          <Button
            className="primary-button"
            variant="contained"
            onClick={() => {
              setShowAddCategoryModal(true);
            }}
          >
            Add
          </Button>
        </Col>
      </Row>

      <DeleteModalCategory
        deleteModalVisible={deleteModalVisible}
        setDeleteModalVisible={setDeleteModalVisible}
        handleDelete={handleDelete}
        section={section}
        selectedItem={selectedItem}
      />

      <AddDietCusiniesModal
        showAddModalForDietCusines={showAddModalForDietCusines}
        setShowAddModalForDietCusines={setShowAddModalForDietCusines}
        currentCategory={currentCategory}
        fetchDietaryPreferences={fetchDietaryPreferences}
        fetchCuisines={fetchCuisines}
      />

      <AddCategoryModal
        showAddCategoryModal={showAddCategoryModal}
        setShowAddCategoryModal={setShowAddCategoryModal}
        fetchCategories={fetchCategories}
      />
    </div>
  );
};

export default Category;
